import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Brand from "@/components/Brand";
import RouteMap from "@/components/RouteMap";
import Ticker from "@/components/Ticker";
import GapMonuments from "@/components/GapMonuments";
import IEvsUK from "@/components/IEvsUK";

const MAILTO = "mailto:hello@traeva.ie?subject=Bearing%20request&body=Brand%20website%3A%20";

export default function Home() {
  return (
    <main data-testid="home-page" className="pt-[88px] md:pt-[72px]">

      {/* ────────── HERO ────────── */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden bg-navy"
        style={{
          backgroundImage:
            "radial-gradient(1200px 700px at 78% 18%,rgba(30,58,88,.55),transparent 60%), radial-gradient(900px 500px at 12% 88%,rgba(184,90,72,.10),transparent 55%)",
        }}
      >
        <RouteMap />

        <div className="relative z-[2] w-full max-w-[1180px] mx-auto px-6 lg:px-11 pt-32 pb-16">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-8.5" data-testid="hero-kicker">
            <span className="w-9 h-px bg-coral" />
            Amazon EU · Operator-led · Dublin
            <em className="not-italic" style={{ color: "rgba(255,255,255,.35)" }}>— est. on 13 years of doing it</em>
          </div>

          <h1
            className="font-serif-i italic text-white leading-[.93] tracking-[-.04em] mb-9"
            style={{ fontSize: "clamp(52px, 9.2vw, 128px)" }}
            data-testid="hero-headline"
          >
            <span className="hl-line"><i>The biggest market</i></span>
            <span className="hl-line"><i>your brand has never entered.</i></span>
            <span className="hl-line"><i className="text-coral">Ireland is the route.</i></span>
          </h1>

          <p className="max-w-[540px] font-light leading-[1.75] mb-11"
             style={{ fontSize: "clamp(15px, 1.6vw, 18px)", color: "rgba(255,255,255,.52)" }}>
            <Brand variant="dark" /> takes consumer brands from zero to live on Amazon Germany, France, and
            the Netherlands — through the only English-speaking country inside the EU customs
            union.{" "}
            <strong className="text-white font-medium">Zero import duty. One VAT registration. One operator, start to finish.</strong>
          </p>

          <div className="flex flex-wrap gap-3.5 items-center">
            <a
              href={MAILTO}
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 bg-coral text-white text-[14px] font-medium px-8 py-4 transition-transform hover:-translate-y-0.5 hover:bg-coral-dark"
            >
              Request your free Bearing <ArrowRight size={16} strokeWidth={1.6} />
            </a>
            <Link
              to="/how-it-works"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 text-[14px] font-medium px-8 py-4 border transition-colors"
              style={{ color: "rgba(255,255,255,.8)", borderColor: "rgba(255,255,255,.25)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#FFFFFF";
                e.currentTarget.style.color = "#FFFFFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,.25)";
                e.currentTarget.style.color = "rgba(255,255,255,.8)";
              }}
            >
              See every price we charge
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 z-[2]">
          <Ticker variant="dark" />
        </div>
      </section>

      {/* ────────── CH 01 — THE GAP ────────── */}
      <section className="bg-paper text-ink border-b border-line" data-testid="the-gap-section" id="gap">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
              <span className="w-9 h-px bg-coral" />
              Chapter 01 — The structural advantage
            </p>
            <h2 className="font-serif-i italic text-[34px] sm:text-[44px] lg:text-[64px] leading-[.98] tracking-[-.035em] max-w-[880px] mb-6 text-ink">
              Your competitors don’t know
              <br />
              <em className="text-navy-mid">this exists.</em>
            </h2>
            <p className="text-[16px] font-light leading-[1.78] max-w-[600px] mb-14 text-ink-muted">
              Where your goods route from decides your costs, your speed, and your paperwork in
              every EU market.{" "}
              <strong className="text-ink font-medium">
                Routed through Ireland, the numbers stop looking like everyone else&rsquo;s.
              </strong>
            </p>
          </Reveal>

          <GapMonuments />
          <IEvsUK />
        </div>
      </section>

      {/* ────────── HOW IT WORKS — GLIMPSE ────────── */}
      <section className="bg-surface text-ink border-b border-line" data-testid="how-it-works-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-6">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
                <span className="w-9 h-px bg-coral" />
                Fares · Crossings · Bearings
              </p>
              <h2 className="font-serif-i italic text-[34px] sm:text-[44px] lg:text-[60px] leading-[.98] tracking-[-.035em] text-ink">
                Four ways to travel.
                <br />
                <em className="text-navy-mid">Every price on the ticket.</em>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.08}>
              <p className="text-ink-muted text-lg leading-[1.78]">
                The Bearing is free. EU Launch is €12,000. Amazon.ie is €3,000. Growth Partnership
                is €1,800/month with a capped success fee. Independent doors — none requires
                another first.
              </p>
              <Link
                to="/how-it-works"
                data-testid="how-it-works-explore-link"
                className="mt-10 inline-flex items-center gap-2 bg-ink text-paper text-[14px] font-medium px-8 py-4 transition-colors hover:bg-navy"
              >
                See the full fares <ArrowRight size={16} strokeWidth={1.6} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ────────── OPERATOR TEASE ────────── */}
      <section className="bg-paper text-ink border-b border-line" data-testid="operator-band">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-6">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
                <span className="w-9 h-px bg-coral" />
                The operator
              </p>
              <h2 className="font-serif-i italic text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-.035em] text-balance text-ink">
                Built by someone who has
                <br />
                run the <em className="text-navy-mid">machine.</em>
              </h2>
              <p className="mt-10 text-ink-muted leading-[1.78] max-w-xl">
                Thirteen years operating Amazon EU categories — across eight marketplaces,
                multiple consumer categories, and a portfolio that peaked at €25M. <Brand /> is
                what gets built when an experienced operator chooses to work for a small number of
                brands, instead of a large company.
              </p>
            </Reveal>

            <div className="lg:col-span-6 lg:col-start-7">
              <ul className="border-t border-line">
                {[
                  { v: "13 yrs", l: "Operating Amazon EU categories at scale" },
                  { v: "€25M", l: "Largest portfolio managed — six EU markets simultaneously" },
                  { v: "6+1", l: "Markets run — DE · FR · IT · ES · NL · SE · IE" },
                  { v: "DUB", l: "Dublin · at the start of the route every client brand travels" },
                ].map((row, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <li
                      data-testid={`operator-row-${i}`}
                      className="grid grid-cols-12 items-baseline gap-4 sm:gap-6 py-7 border-b border-line"
                    >
                      <span className="col-span-3 sm:col-span-2 font-serif-i italic text-[26px] sm:text-[32px] text-ink leading-none">
                        {row.v}
                      </span>
                      <span className="col-span-9 sm:col-span-10 text-[14px] sm:text-[15px] text-ink-muted leading-relaxed">
                        {row.l}
                      </span>
                    </li>
                  </Reveal>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-2 font-medium text-[13.5px] text-ink pb-1 border-b-[1.5px] border-coral hover:text-coral transition-colors"
                data-testid="operator-link"
              >
                Read the operator’s manifest
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── FINALE ────────── */}
      <section
        className="relative overflow-hidden bg-navy text-white text-center"
        data-testid="home-finale"
      >
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(900px 500px at 50% 110%,rgba(184,90,72,.16),transparent 60%)" }} />
        <div className="relative z-[2] max-w-[880px] mx-auto px-6 lg:px-10 py-20 sm:py-24">
          <Reveal>
            <span
              className="inline-block font-serif-i italic text-verdict-go border-[2.5px] border-verdict-go px-6 py-2.5 rounded-[5px] mb-11 relative -rotate-6 tracking-[-.01em]"
              style={{ fontSize: "clamp(15px,1.5vw,19px)" }}
              data-testid="home-finale-stamp"
            >
              <span
                aria-hidden="true"
                className="absolute inset-1 border border-verdict-go rounded-[2px] opacity-50 pointer-events-none"
              />
              CLEARED FOR ASSESSMENT
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif-i italic text-[40px] sm:text-6xl lg:text-[80px] leading-[.96] tracking-[-.04em] mb-7"
                data-testid="home-finale-headline">
              Find out where your brand stands.
              <br />
              It costs <em className="text-coral">nothing.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-[16px] font-light leading-[1.78] max-w-[520px] mx-auto mb-12" style={{ color: "rgba(255,255,255,.5)" }}>
              Send one line — your website address. <Brand variant="dark" /> will prepare a free Bearing: an
              honest read on your Amazon EU opportunity, ending in a stamp.{" "}
              <strong className="text-white font-medium">No call. No form. No obligation. Yours either way.</strong>
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <a
              href={MAILTO}
              data-testid="home-finale-cta"
              className="inline-flex items-center gap-2 bg-coral text-white text-[15px] font-medium px-10 py-[18px] transition-transform hover:-translate-y-0.5 hover:bg-coral-dark"
            >
              Request your free Bearing <ArrowRight size={16} strokeWidth={1.6} />
            </a>
            <p className="mt-7 font-mono text-[11px] tracking-[0.2em] uppercase" style={{ color: "rgba(255,255,255,.35)" }}>
              Or simply write to{" "}
              <a href="mailto:hello@traeva.ie" className="border-b border-coral pb-[1px]" style={{ color: "rgba(255,255,255,.7)" }}>
                hello@traeva.ie
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
